import React, { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  CardMedia,
  Divider,
  Stack,
  Tooltip,
  useTheme,
} from "@mui/material";
import AllSidesRating from "components/base/all-sides/AllSidesRating";
import NewsguardScore from "components/base/NewsguardScore";
import defaultBackground from "assets/images/image-placeholder.png";
import {
  setCollectionId,
  setCollectionSourceId,
  setEntityName,
  setIsCitationAvailable,
  setIsSidebarOpen,
  setIsSourceSidebarOpen,
  setIsSummaryAvailable,
  setLevel,
  setReadingLevelAvailable,
  setSelectedBookmarkOption,
  setSourceUrl,
  setSummaryInfoData,
} from "context/features/dataSlice";
import { useDispatch } from "react-redux";

import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { ReactComponent as CollectionsLogo } from "assets/icons/bookmark-blue-icon.svg";
import { ReactComponent as CollectionsLogoHover } from "assets/icons/bookmark-hover-icon.svg";
import { HiOutlineLockClosed } from "react-icons/hi2";
import { logEvent } from "services/amplitude.service";
import SearchResultLink from "features/SourceSearch/components/SearchResultLink";
import FileTypeIcon from "components/base/FileTypeIcon";
import { devViewPdf } from "config";
import { showNewsguard } from "utils/show-ratings";
import SmileyIcon from "features/News/components/smiley-icon";
import AccessibleIconButton from "components/base/AccessibleIconButton";

const NewsCard = ({ index, item, cardRef, category, query }) => {
  const { palette } = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const isSpecialIndex = index % 21 === 0;

  // const imageUrl = item?.openGraphImage?.contentUrl
  //   ? item?.openGraphImage?.contentUrl
  //   : defaultBackground;

  const dispatch = useDispatch();

  const openSourceSidebar = () => {
    logEvent("click_card_header", {
      host: item.hostName,
      entity: item.entityName,
    });
    dispatch(setIsSourceSidebarOpen(true));
    dispatch(setSourceUrl(item.corpusUrl));
    dispatch(setEntityName(item.entityName));
  };

  const openSidebar = () => {
    logEvent("open_bookmark_popup", {
      url: item.url,
      query: query,
      entity: item.entityName,
      category: category,
      type: "pulse",
      mediabiasrating: item.mediaBiasRating ? item.mediaBiasRating : "NA",
      score: showNewsguard(item.score) ? item.score : "NA",
    });
    dispatch(setSelectedBookmarkOption("notes"));
    dispatch(setLevel(""));
    dispatch(setCollectionSourceId(item.collectionSourceId));
    dispatch(
      setSummaryInfoData({
        publisher: item.entityName,
        title: item.name,
        published_date: item.datePublishedDisplayText,
        url: item.url,
        utc_date_published: item.publishingDate,
      })
    );
    dispatch(setCollectionId(item.collectionId));
    dispatch(setIsCitationAvailable(item.isCitationAvailable));
    dispatch(setIsSummaryAvailable(item.isSummaryAvailable));
    dispatch(setReadingLevelAvailable(item.isReadingLevelAvailable));
    dispatch(setIsSidebarOpen(true));
  };

  const BookmarkOptions = () => {
    return (
      <Stack
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => {
          openSidebar();
          setIsHovered(false);
        }}
        sx={{
          cursor: "pointer",
          height: "40%",
          pl: 1,
          pr: 1,
          borderRadius: 1,
          "&:hover": {
            backgroundColor: "secondary.main",
          },
        }}
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        gap={1}
      >
        {item.collectionSourceId ? (
          <CollectionsLogo
            onClick={openSidebar}
            alt="Collections"
            style={{
              width: 18,
              height: 18,
              fill: "blue",
              color: "blue",
            }}
          />
        ) : (
          <Box
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            sx={{
              width: 17,
              height: 17,
            }}
          >
            {isHovered && (
              <CollectionsLogoHover
                onClick={openSidebar}
                alt="Collections"
                style={{
                  width: 17,
                  height: 17,
                  fill: "blue",
                  color: "blue",
                }}
              />
            )}
          </Box>
        )}
        <AccessibleIconButton
          title="Open Bookmark"
          sx={{
            p: 0,
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}
        >
          <MoreHorizIcon />
        </AccessibleIconButton>
      </Stack>
    );
  };

  const ImageSection = ({ ...props }) => {
    const imageUrl = item?.openGraphImage?.contentUrl;

    const [errored, setErrored] = useState(false);

    function onError({ currentTarget }) {
      if (!errored) {
        setErrored(true);
        currentTarget.src = defaultBackground;
        currentTarget.alt = "Image not found";
      }
    }
    return (
      <CardMedia
        component="img"
        sx={{
          aspectRatio: 1,
          borderRadius: "10px",
        }}
        image={imageUrl == null ? defaultBackground : imageUrl}
        alt={imageUrl == null ? "Image not found" : item?.name}
        onError={onError}
        {...props}
      />
    );
  };

  return (
    <Grid
      ref={cardRef}
      container
      spacing={2}
      sx={{ height: isSpecialIndex ? "36vh" : "auto" }}
    >
      {isSpecialIndex && (
        <Grid
          item
          xs={6}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ImageSection
            sx={{ height: "30vh", width: "100%", borderRadius: 1 }}
          />
        </Grid>
      )}
      <Grid
        item
        xs={isSpecialIndex ? 6 : 9}
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
          py: 1,
        }}
      >
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          onClick={openSourceSidebar}
          id={index === 0 ? "pulse-article-source" : null}
          sx={{
            borderRadius: 1,
            px: "1rem",
            py: 0.4,
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "secondary.main",
            },
          }}
        >
          <Grid sx={{ mb: 1 }} item>
            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
                color: "grey.600",
              }}
            >
              {item.entityName}
            </Typography>
          </Grid>
          <Grid item>
            <Grid container spacing={1}>
              {item.mediaBiasRating && (
                <Grid item id="pulse-source-allsides">
                  <AllSidesRating activeCategory={item.mediaBiasRating} />
                </Grid>
              )}
              {showNewsguard(item.score) && (
                <Grid item id="pulse-source-newsguard">
                  <NewsguardScore score={Number(item.score)} size="small" />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Box sx={{ px: "1rem" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start",
            }}
          >
            <SearchResultLink
              url={item.url}
              trackingCat="click_resultset_title"
              trackingOptions={{
                url: item.url,
                query: query,
                entity: item.entityName,
                category: category ? category : item.modelCategoryName,
                type: "pulse",
                mediabiasrating: item.mediaBiasRating
                  ? item.mediaBiasRating
                  : "NA",
                score: showNewsguard(item.score) ? item.score : "NA",
                title: item.name,
                date_published: item.publishingDate,
              }}
              variant={isSpecialIndex ? "h4" : "body4"}
              sx={{
                cursor: "pointer",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: { xs: 3 },
                WebkitBoxOrient: "vertical",
                // lineHeight: { xs: "2vh", sm: "2vh" },
                maxWidth: "90%",
                "&:hover": {
                  color: palette.primary.main,
                },
                mb: 1,
              }}
            >
              {item.name}
            </SearchResultLink>
            {devViewPdf && <FileTypeIcon url={item.url} sx={{ flexShrink: 0, mt: "2px" }} />}
            {item.partner ? (
              <SmileyIcon />
            ) : (
              item.paywall && (
                <Tooltip
                  placement="bottom-start"
                  title="This source may limit your access without a subscription"
                >
                  <Box sx={{ cursor: "pointer" }}>
                    <HiOutlineLockClosed />
                  </Box>
                </Tooltip>
              )
            )}
          </Box>
          {item.snippet && (
            <Typography
              sx={{
                "&:hover": {
                  color: "rgb(18,89,151)",
                },
              }}
            >
              {item.snippet}
            </Typography>
          )}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="body2" color="textSecondary">
              {item.datePublishedDisplayText &&
                `${item.datePublishedDisplayText}`}
            </Typography>
            <BookmarkOptions />
          </Stack>
        </Box>
        <Divider />
      </Grid>
      {!isSpecialIndex && (
        <Grid
          item
          xs={3}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ImageSection sx={{ height: "12vh", borderRadius: 1 }} />
        </Grid>
      )}
    </Grid>
  );
};

export default NewsCard;
